import { useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useAnalytics } from '@backstage/core-plugin-api';
import type { SearchResult } from '@backstage/plugin-search-common';
import { useSearchModal } from '@backstage/plugin-search';
import { SearchBar, useSearch } from '@backstage/plugin-search-react';
import { QuickSearchKeyboardHints } from './QuickSearchKeyboardHints';
import { QuickSearchResultItem } from './QuickSearchResultItem';
import { RecentSearchItem } from './RecentSearchItem';
import { makeVisit, RecentVisit } from './recentSearches';
import { resolveLocation } from './resolveResultLocation';
import { extractResultMetadata } from './searchResultMetadata';
import { useRecentSearches } from './useRecentSearches';

export type QuickSearchModalProps = {
  toggleModal: () => void; // provided by SearchModal via the children render-prop
};

export const QuickSearchModal = ({ toggleModal }: QuickSearchModalProps) => {
  // Read the palette mode from the nearest parent MUI v7 ThemeProvider
  // (Backstage's UnifiedThemeProvider). useTheme() here reads the parent
  // context because our own ThemeProvider hasn't wrapped anything yet.
  // We need this because SearchModal renders a MUI v4 Dialog whose
  // ThemeProvider doesn't produce a v7 theme — we must create one ourselves,
  // and it must match the user's current light/dark preference.
  const parentTheme = useTheme();
  const modalTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: parentTheme.palette.mode ?? 'light',
          primary: { main: '#6d00cc' },
        },
      }),
    [parentTheme.palette.mode],
  );

  const recent = useRecentSearches();

  const { result, term } = useSearch();
  const { loading, error, value } = result;
  const items = value?.results ?? [];

  // Unified item list: live search results when a query is active, recent
  // visits when the input is empty. The keyboard handler always operates on
  // displayItems so ↑/↓/Enter work identically for both sources.
  const displayItems = term ? items : recent.visits;

  // Read hidden/open state so we can detect each open transition.
  const { state: modalState } = useSearchModal();

  const [selectedIndex, setSelectedIndex] = useState(0);
  const itemRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  // Ref to the underlying <input> element for imperative focus + select.
  const inputRef = useRef<HTMLInputElement>(null);
  const analytics = useAnalytics();

  // Tracks whether the current palette session ended in a result selection,
  // plus the latest term/result count, so closing without a selection can be
  // reported as `search_abandon`. Per-keystroke `search` events are collapsed
  // into one committed search by SearchCommitAnalyticsApi.
  const sessionRef = useRef({ selected: false, abandonReported: false });
  const latestRef = useRef({ term, count: value?.numberOfResults });
  latestRef.current = { term, count: value?.numberOfResults };

  const reportAbandon = () => {
    const session = sessionRef.current;
    const { term: lastTerm, count } = latestRef.current;
    if (session.selected || session.abandonReported || !lastTerm.trim()) {
      return;
    }
    session.abandonReported = true;
    // Query text is intentionally not sent to analytics (stored server-side).
    analytics.captureEvent('search_abandon', 'search-palette', {
      value: count,
      attributes:
        typeof count === 'number'
          ? { resultCount: count, zeroResults: count === 0 }
          : {},
    });
  };
  const reportAbandonRef = useRef(reportAbandon);
  reportAbandonRef.current = reportAbandon;

  // The Dialog may unmount its content on close; report abandonment then too.
  useEffect(() => () => reportAbandonRef.current(), []);

  // Reset highlight to top whenever the query, result set, or recent-visits
  // list changes. recent.visits.length is included so that deleting a row via
  // × or Clear doesn't leave selectedIndex pointing past the end of the array
  // (arrow nav uses modulo so it recovers, but the visual highlight would jump).
  useEffect(() => {
    setSelectedIndex(0);
  }, [term, value, recent.visits.length]);

  // Scroll the highlighted row into view when the user navigates with ↑/↓.
  useEffect(() => {
    itemRefs.current[selectedIndex]?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  // On every open (hidden → visible): reset selection and focus+select the
  // input so any previous query text is ready to be overwritten.
  const prevHiddenRef = useRef(true);
  useEffect(() => {
    const justOpened = prevHiddenRef.current && !modalState.hidden;
    const justClosed = !prevHiddenRef.current && modalState.hidden;
    prevHiddenRef.current = modalState.hidden;
    if (justClosed) reportAbandonRef.current();
    if (!justOpened) return;

    sessionRef.current = { selected: false, abandonReported: false };
    analytics.captureEvent('search_open', 'search-palette');
    setSelectedIndex(0);
    // rAF defers focus until after the Dialog entrance transition so the
    // browser's focus management doesn't immediately fight us.
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    // analytics is a stable ref from useAnalytics(); re-runs are harmless
    // because the effect only acts on hidden/visible transitions.
  }, [modalState.hidden, analytics]);

  // Volatile values needed inside the keydown handler. Using a ref avoids
  // stale closures without re-registering the listener on every render.
  // `displayItems` (not raw `items`) is used so ↑/↓/Enter work over recents
  // when the query is empty, exactly as they do for live results.
  // `removeHighlightedRecent` is populated only when the recents panel is
  // active so Ctrl+X can delete the highlighted entry without the handler
  // needing to know about the recents/live distinction.
  const removeHighlightedRecent =
    !term && recent.visits.length > 0
      ? (idx: number) => {
          const visit = recent.visits[idx];
          if (visit) recent.remove(visit.location);
        }
      : null;

  const handlerStateRef = useRef({
    selectedIndex,
    items: displayItems,
    toggleModal,
    removeHighlightedRecent,
  });
  handlerStateRef.current = {
    selectedIndex,
    items: displayItems,
    toggleModal,
    removeHighlightedRecent,
  };

  // Capture-phase keydown listener on window — fires before React synthetic
  // events, so arrow navigation and Escape work regardless of which element
  // has focus and regardless of stopPropagation calls in child components
  // (e.g. SearchBar's own Escape / clear handler).
  useEffect(() => {
    if (modalState.hidden) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      const {
        selectedIndex: idx,
        items: its,
        toggleModal: close,
      } = handlerStateRef.current;
      const maxIdx = its.length;

      // Ctrl+N / Ctrl+P: Emacs-style nav aliases for ↓ / ↑.
      // Checked before the key switch so the Ctrl modifier short-circuits
      // before falling through to unhandled keys.
      if (e.ctrlKey && (e.key === 'n' || e.key === 'p')) {
        if (maxIdx === 0) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.key === 'n') setSelectedIndex(i => (i + 1) % maxIdx);
        else setSelectedIndex(i => (i - 1 + maxIdx) % maxIdx);
        return;
      }

      // Ctrl+X: delete the highlighted recent entry (no-op on live results).
      if (e.ctrlKey && e.key === 'x') {
        const { removeHighlightedRecent: rmv } = handlerStateRef.current;
        if (!rmv) return;
        e.preventDefault();
        e.stopPropagation();
        rmv(idx);
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          if (maxIdx === 0) return;
          e.preventDefault();
          e.stopPropagation();
          setSelectedIndex(i => (i + 1) % maxIdx);
          return;
        case 'ArrowUp':
          if (maxIdx === 0) return;
          e.preventDefault();
          e.stopPropagation();
          setSelectedIndex(i => (i - 1 + maxIdx) % maxIdx);
          return;
        case 'Enter':
          if (maxIdx === 0) return;
          e.preventDefault();
          e.stopPropagation();
          // Clamp to last valid index in case selectedIndex hasn't been reset
          // yet after results shrink (the reset effect fires after render).
          itemRefs.current[Math.min(idx, maxIdx - 1)]?.click();
          return;
        case 'Escape':
          // Close regardless of whether there are results.
          e.preventDefault();
          e.stopPropagation();
          close();
          return;
        default:
          return;
      }
    };

    // true = capture phase: fires before the event reaches any target,
    // bypassing stopPropagation in React subtrees.
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [modalState.hidden]);

  // Called when the user activates a live search result (click or Enter).
  // Records the visit for the recents list and tags the analytics event with
  // source:'palette-live' so it's distinguishable from recent-visit selects.
  // NOTE: `meta` is not available in this scope — it lives inside
  // QuickSearchResultItem — so we recompute it here via extractResultMetadata.
  const onActivateLive = (rank: number, item: SearchResult) => {
    sessionRef.current.selected = true;
    analytics.captureEvent('select', item.document.title, {
      value: rank,
      attributes: { source: 'palette-live', resultType: item.type },
    });
    const meta = extractResultMetadata(item);
    recent.recordVisit(
      makeVisit({
        title: item.document.title,
        location: resolveLocation(item),
        kind: meta.kind,
        sourceLabel: meta.sourceLabel,
        visitedAt: Date.now(),
      }),
    );
    toggleModal();
  };

  // Called when the user activates an entry from the recents list.
  // Navigation is handled by the underlying Link; we just record analytics
  // and close the modal.
  const onActivateRecent = (rank: number, v: RecentVisit) => {
    sessionRef.current.selected = true;
    analytics.captureEvent('select', v.title, {
      value: rank,
      attributes: { source: 'palette-recent' },
    });
    toggleModal();
  };

  return (
    <ThemeProvider theme={modalTheme}>
      <Box
        // height: 100% fills the Dialog Paper (which SearchModal sizes to
        // calc(100% - 128px)). A fixed 70vh left a blank gap below the hints
        // footer whenever the Paper was taller than 70vh.
        sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        {/* Search input + close button */}
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {/* inputRef attaches to the native <input> so we can imperatively
            focus + selectAll on each modal open.
            style (not sx) because SearchBarBaseProps extends MUI v4
            TextFieldProps which doesn't declare the sx prop. */}
          <Box sx={{ flex: 1 }}>
            <SearchBar
              inputRef={inputRef}
              inputProps={{ autoComplete: 'off' }}
            />
          </Box>
          <IconButton onClick={toggleModal} aria-label="Close search">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Result list.
          A strict term ternary ensures only one row type (QuickSearchResultItem
          or RecentSearchItem) ever populates itemRefs at a time. Mixing both
          would cause selectedIndex to index one array while the refs come from
          another. */}
        <Box
          role="listbox"
          aria-label="Search results"
          sx={{ flex: 1, overflow: 'auto', px: 1 }}
        >
          {term ? (
            // ── Live search results ─────────────────────────────────────────
            <>
              {loading && items.length === 0 && (
                <Stack>
                  <Skeleton variant="text" sx={{ mx: 2, my: 1 }} />
                  <Skeleton variant="text" sx={{ mx: 2, my: 1 }} />
                  <Skeleton variant="text" sx={{ mx: 2, my: 1 }} />
                </Stack>
              )}
              {error && (
                <Alert severity="error" sx={{ m: 2 }}>
                  Search failed. Try again.
                </Alert>
              )}
              {!loading && !error && items.length === 0 && (
                <Typography sx={{ p: 2 }}>No results for "{term}"</Typography>
              )}
              {items.map((item, i) => (
                <QuickSearchResultItem
                  key={item.document.location}
                  ref={(el: HTMLAnchorElement | null) => {
                    itemRefs.current[i] = el;
                  }}
                  result={item}
                  rank={i + 1}
                  selected={i === selectedIndex}
                  onActivate={() => onActivateLive(i + 1, item)}
                />
              ))}
            </>
          ) : (
            // ── Empty query: recents or placeholder ─────────────────────────
            <>
              {!recent.hydrated && null /* wait for storage; avoids flicker */}
              {recent.hydrated && recent.visits.length === 0 && (
                <Typography color="text.secondary" sx={{ p: 2 }}>
                  Start typing to search…
                </Typography>
              )}
              {recent.hydrated && recent.visits.length > 0 && (
                <>
                  <Box
                    sx={{
                      px: 2,
                      pt: 1.5,
                      pb: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ flex: 1, lineHeight: 1 }}
                    >
                      Recent
                    </Typography>
                    <Button size="small" onClick={recent.clear}>
                      Clear
                    </Button>
                  </Box>
                  {recent.visits.map((v, i) => (
                    <RecentSearchItem
                      key={v.location}
                      ref={(el: HTMLAnchorElement | null) => {
                        itemRefs.current[i] = el;
                      }}
                      visit={v}
                      rank={i + 1}
                      selected={i === selectedIndex}
                      onActivate={() => onActivateRecent(i + 1, v)}
                      onRemove={() => recent.remove(v.location)}
                    />
                  ))}
                </>
              )}
            </>
          )}
        </Box>

        {/* Sticky keyboard hints footer + view-all link.
          The link is only shown when there is an active query; navigation
          to /search triggers the modal's location-change listener which
          sets hidden:true, so no explicit toggleModal call is needed. */}
        <QuickSearchKeyboardHints
          viewAllHref={
            term ? `/search?query=${encodeURIComponent(term)}` : undefined
          }
          recentsActive={!term && recent.visits.length > 0}
        />
      </Box>
    </ThemeProvider>
  );
};
